import { useState } from 'react';
import { Settings, Save, RotateCcw, X, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { usePromotional } from '../../contexts/PromotionalContext';
import { cn } from '@/utils/cn';

interface PromotionalAdminProps {
  className?: string;
}

const PromotionalAdmin = ({ className }: PromotionalAdminProps) => {
  const { config, updateConfig, resetToDefaults, isActive, timeRemaining } = usePromotional();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const isProduction = import.meta.env.PROD;

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    updateConfig({ [field]: new Date(value) });
  };

  const formatDateForInput = (date: Date) => {
    return date.toISOString().slice(0, 16);
  };

  const getStatusColor = () => {
    if (!config.isEnabled) return 'bg-gray-500';
    if (isActive) return 'bg-green-500';
    return 'bg-orange-500';
  };

  const getStatusText = () => {
    if (!config.isEnabled) return 'Disabled';
    if (isActive) return 'Active';
    return 'Scheduled';
  };

  // Hide admin panel in production
  if (isProduction) {
    return null;
  }

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 left-4 z-50 bg-gray-900 text-white p-3 rounded-full shadow-2xl hover:bg-gray-800 transition-all transform hover:scale-110"
        title="Open Promotional Settings (Development Only)"
      >
        <Settings className="h-5 w-5" aria-label="Settings icon" />
      </button>
    );
  }

  return (
    <div className={cn(
      'fixed bottom-4 left-4 z-50 bg-white rounded-2xl shadow-2xl border-2 border-gray-200 max-w-sm w-full mx-4 sm:mx-0',
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <Settings className="h-5 w-5 text-gray-700" aria-label="Settings icon" />
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Promotional Settings</h3>
            <div className="flex items-center gap-2 mt-1">
              <div className={cn('w-2 h-2 rounded-full', getStatusColor())}></div>
              <span className="text-xs text-gray-600">{getStatusText()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-1 hover:bg-gray-200 rounded-full transition-all"
            title={isOpen ? 'Minimize' : 'Expand'}
          >
            <ChevronDown 
              className={cn('h-4 w-4 text-gray-600 transition-transform', isOpen ? 'rotate-180' : '')}
            />
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="p-1 hover:bg-gray-200 rounded-full transition-all"
            title="Hide"
          >
            <X className="h-4 w-4 text-gray-600" aria-label="X icon" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isOpen && (
        <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
          {/* Development Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs">
            <div className="font-medium text-yellow-800 mb-1">🚧 Development Mode</div>
            <div className="text-yellow-700">
              Admin panel changes are for testing only. In production, promotions are controlled via environment variables.
            </div>
          </div>

          {/* Status & Time Remaining */}
          {isActive && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-sm font-medium text-green-800 mb-1">Active Promotion</div>
              <div className="text-xs text-green-600">
                {timeRemaining.days}d {timeRemaining.hours}h {timeRemaining.minutes}m {timeRemaining.seconds}s remaining
              </div>
            </div>
          )}

          {/* Master Toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Enable Promotion</label>
            <button
              onClick={() => updateConfig({ isEnabled: !config.isEnabled })}
              className={cn(
                'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                config.isEnabled ? 'bg-green-600' : 'bg-gray-200'
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  config.isEnabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>

          {/* Coupon Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
            <input
              type="text"
              value={config.couponCode}
              onChange={(e) => updateConfig({ couponCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter coupon code"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={config.description}
              onChange={(e) => updateConfig({ description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Promotion description"
            />
          </div>

          {/* Discount Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
            <input
              type="number"
              min="0"
              max="100"
              value={config.discountPercentage}
              onChange={(e) => updateConfig({ discountPercentage: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="datetime-local"
                value={formatDateForInput(config.startDate)}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="datetime-local"
                value={formatDateForInput(config.endDate)}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Visibility Controls */}
          <div className="space-y-3 pt-2 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">Display Options</div>
            
            {[
              { key: 'showHeroBanner', label: 'Hero Banner', description: 'Show in homepage hero section' },
              { key: 'showStickyHeader', label: 'Sticky Header', description: 'Show sticky banner at top' },
              { key: 'showFloatingWidget', label: 'Floating Widget', description: 'Show floating popup widget' },
              { key: 'showInlinePromotions', label: 'Inline Promotions', description: 'Show in content sections' },
            ].map(({ key, label, description }) => (
              <div key={key} className="flex items-start gap-3">
                <button
                  onClick={() => updateConfig({ [key]: !config[key as keyof typeof config] })}
                  className="mt-0.5"
                >
                  {config[key as keyof typeof config] ? (
                    <Eye className="h-4 w-4 text-green-600" aria-label="Eye icon" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-gray-400" aria-label="EyeOff icon" />
                  )}
                </button>
                <div>
                  <div className="text-sm font-medium text-gray-700">{label}</div>
                  <div className="text-xs text-gray-500">{description}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Urgency Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Urgency Message</label>
            <input
              type="text"
              value={config.urgencyMessage}
              onChange={(e) => updateConfig({ urgencyMessage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Urgency message"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-gray-200">
            <button
              onClick={resetToDefaults}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
            >
              <RotateCcw className="h-4 w-4" aria-label="RotateCcw icon" />
              Reset
            </button>
            <button
              onClick={() => {
                // Save is automatic via context, so just show feedback
                const button = document.activeElement as HTMLButtonElement;
                const originalText = button.textContent;
                button.textContent = 'Saved!';
                setTimeout(() => {
                  if (button) button.textContent = originalText;
                }, 1000);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all"
            >
              <Save className="h-4 w-4" aria-label="Save icon" />
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionalAdmin;